// Libraries
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
// Services

import {ApiService, SettingService} from '../../common/modules/core/services';
// Models
import {
  StudentsInClass,
  ClassItem,
  BookingItem,
  ClassList,
  ClassFilter,
  ClassChild,
  AvailableClass,
  ClassPayment,
  BookingInfo,
  Reservation,
  CancelReservation
} from '../models';

@Injectable()
export class ClassService {
  // Api Domain Url
  private apiUrl = '';
  private classUrl = '/asa-classes';
  // Url List for Class Parent Site
  private classesChildrenUrl = '/asa-children';
  private availableClassesUrl = '/asa-students';
  private reservationUrl = '/asa-students';
  private paymentUrl = '/asa-bookings';

  constructor(private apiService: ApiService,
              private settingService: SettingService) {
    // Get API service domain for communate.
    this.apiUrl = this.settingService.getSetting('api_service_domain');
  }

  /**
   * Get children list of logged in parent on Class modules.
   * @returns {ClassChild[]} : list of ClassChild item.
   */
  getClassesChildrenList(): Observable<ClassChild[]> {
    const requestUrl = this.apiUrl + this.classesChildrenUrl;
    return this.apiService.get<ClassChild[]>(requestUrl);
  }

  getClassList(filter: ClassFilter): Observable<ClassList> {
    let requestUrl = this.apiUrl + this.classUrl;
    let hasFilter = false;

    // Setup filter conditions
    for (let condition in filter) {
      if (filter[condition]) {
        if (hasFilter) {
          requestUrl += '&';
        }
        else {
          requestUrl += '?';
          hasFilter = true;
        }

        requestUrl += condition + '=' + filter[condition];
      }
    }

    return this.apiService.get<ClassList>(requestUrl);
  }

  getFullClassList(): Observable<ClassList> {
    let requestUrl = this.apiUrl + this.classUrl;
    return this.apiService.get<ClassList>(requestUrl);
  }

  /**
   * Get open Classes list that are available for a specific student.
   * @param {number} studentId : Id of the student.
   * @returns {AvailableClass[]} : list of open classes.
   */
  getAvailableClassesList(studentId: number): Observable<AvailableClass[]> {
    const requestUrl = this.apiUrl + this.availableClassesUrl + "/" + studentId + "/" + "classes";

    return this.apiService.get<AvailableClass[]>(requestUrl);
  }

  /**
   * Add new reservation into a specific class.
   * @param {ClassReservation} classReservation : class reservation data.
   * @returns {Observable<any>} : request response result (string).
   */
  addClassReservation(classReservation: Reservation): Observable<any> {
    const requestUrl = this.apiUrl + this.reservationUrl + "/" + classReservation.studentId + "/reservations";

    // Post command
    const body = JSON.stringify(classReservation.command)

    return this.apiService.post(requestUrl, body);
  }

  /**
   * Cancel reservation into a specific class.
   * @param {CancelReservation} cancelReservation : cancel reservation.
   * @returns {Observable<any>} : request response result (string).
   */
  cancelClassReservation(cancelReservation: CancelReservation): Observable<any> {
    const requestUrl = this.apiUrl + this.reservationUrl + "/" + cancelReservation.studentId + "/reservations" + "/" + cancelReservation.classId;

    return this.apiService.delete(requestUrl);
  }

  createClass(classItem: ClassItem): Observable<any> {
    const requestUrl = this.apiUrl + this.classUrl;

    // Post content
    const body = JSON.stringify(classItem);

    return this.apiService.post(requestUrl, body);
  }

  editClass(classItem: ClassItem): Observable<any> {
    const requestUrl = this.apiUrl + this.classUrl + '/' + classItem.id;

    const body = JSON.stringify(classItem);

    return this.apiService.patch(requestUrl, body);
  }

  getClassDetail(classId: number): Observable<ClassItem> {
    const requestUrl = this.apiUrl + this.classUrl + '/' + classId;

    return this.apiService.get<ClassItem>(requestUrl);
  }

  bookingClassSubmit(booking: BookingInfo): Observable<any> {
    const requestUrl = this.apiUrl + this.paymentUrl;

    // Post content
    const body = JSON.stringify(booking);

    return this.apiService.post(requestUrl, body);
  }

  // Update Booking class
  bookingClassUpdate(booking: BookingInfo): Observable<any> {
    const requestUrl = this.apiUrl + this.paymentUrl;

    // update content
    const body = JSON.stringify(booking);

    return this.apiService.patch(requestUrl, body);
  }

  /**
   * Get booking class detail for a specific student.
   * @param {number} studentId : Id of the student.
   * @returns {BookingItem[]} : list of open classes.
   */
  bookingClassDetail(studentId: number): Observable<BookingItem> {
    const requestUrl = this.apiUrl + '/' + 'asa-students' + '/' + studentId + '/' + 'bookings';

    return this.apiService.get<BookingItem>(requestUrl);
  }

  // Get specific booking detail
  getBookingDetail(bookingId: number): Observable<any> {
    const requestUrl = this.apiUrl + this.paymentUrl + '/' + bookingId;

    return this.apiService.get<any>(requestUrl);
  }

  // Update sepecific booking detail
  updateBookingDetail(booking: any): Observable<any> {
    const requestUrl = this.apiUrl + this.paymentUrl + '/' + booking.id;

    const body = JSON.stringify(booking.command);

    return this.apiService.patch<any>(requestUrl, body);
  }

  getStudentListInClass(classId: number): Observable<StudentsInClass[]> {
    const requestUrl = this.apiUrl + this.classUrl + "/" + classId + "/students";

    return this.apiService.get<StudentsInClass[]>(requestUrl);
  }


  deleteStudentInClass(classId: number, studentId: number): Observable<any> {
    const requestUrl = this.apiUrl + this.classUrl + "/" + classId + "/students" + "/" + studentId;

    return this.apiService.delete(requestUrl);
  }

  // For payment classes
  paymentClass(paymentInfo: ClassPayment): Observable<any> {
    const requestUrl = this.apiUrl + this.paymentUrl + '/' + paymentInfo.bookingId + '/payments';

    // Post content
    const body = JSON.stringify(paymentInfo.command);

    return this.apiService.post(requestUrl, body);
  }
}


// WEBPACK FOOTER //
// ./src/holiday/services/ClassService.ts